import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import SelectionModal from "../../components/common/modalPopup/selectionModal";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { FdpDriverClientAccount } from "../../app/models/responseModels/loginResponse";
import {
  getfdpDriverClientEmails,
  removeFdpDriverAccount,
  updateFdpDriverSelectedEmailValue,
  updateRequestDriverEmail,
} from "../../app/features/fdp/fdpSlice";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { Chip, Grid, MenuItem, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RtsScanStore from "../rtsScanModule/RtsScanStore";
import { useNavigate } from "react-router-dom";

export interface DriverIDSelectionProps {
  newSelection?: boolean;
  reselection?: boolean;
  setShowDriverSelection: any;
  routeCheckout?: any;
  setShowClientSelection?: any;
}

const DriverIDSelection = (props: DriverIDSelectionProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { rtsUserClient, setRtsUserClient, location } = RtsScanStore();
  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const {
    globallySelectedDriverClientEmail,
    isFdpLoading,
    fdcDriverAllClientEmails,
    driverUserCredsMapping,
  } = useSelector((state: RootState) => state.fdp);

  // states
  const [isDriverSelectionModalOpen, setDriverSelectionModalOpen] =
    useState(true);
  const [disableSubmitOption, setDisableSubmitOption] = useState(true);
  const [fdpDriverClientEmails, setFdpDriverClientEmails] = useState<
    FdpDriverClientAccount[]
  >([]);
  const [isAutoDriverEmailSelection, setIsAutoDriverEmailSelection] =
    useState(false);
  const [selectedDriverEmail, setSelectedDriverEmail] = useState<
    FdpDriverClientAccount | undefined
  >(undefined);
  const [rtsCount, setRtsCountValue] = useState<number>(0);
  const [comment, setComment] = useState("");

  const handleRtsCountChange = (event: any) => {
    const inputVal = event.target.value;
    if (/^\d*$/.test(inputVal)) {
      setRtsCountValue(Number(inputVal));
    }
  };

  const handleCommentChange = (event: any) => {
    const inputVal: string = event.target.value;
    if (/^[a-zA-Z0-9\s.]*$/.test(inputVal)) {
      setComment(inputVal);
    }
  };

  const handleModalClose = (isDriverEmailSelected: boolean = false) => {
    props.setShowDriverSelection(false);
    setDriverSelectionModalOpen(false);
    if (globallySelectedDriverClientEmail) {
      return;
    }
    if ((rtsCount === 0 && props.routeCheckout) || !isDriverEmailSelected) {
      navigate("/");
    }
  };

  const handleChange = (event: any) => {
    const driverEmail = fdpDriverClientEmails.find(
      (item) => item.driverEmail === event.target.value
    );
    if (driverEmail) {
      setSelectedDriverEmail(driverEmail);
    }
  };

  const getfdpDriverEmails = useCallback(async () => {
    try {
      const response = await dispatch(getfdpDriverClientEmails()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpdriverEmailList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverEmailListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getfdpDriverEmails();
    }
    return () => {
      isFunctionRan = false;
      setDriverSelectionModalOpen(true);
    };
  }, [getfdpDriverEmails]);

  useEffect(() => {
    // Check if rtsUserClient exists and then find the driver one to one mapping if exists.
    const driverMatchedAccount = rtsUserClient
      ? (() => {
          const driverMapping = driverUserCredsMapping.find(
            (driverMapping) => driverMapping.clientId === rtsUserClient.id
          );
          // If a matching object is found, update the storeId
          if (driverMapping) {
            return {
              ...driverMapping,
              storeId: location.storePk, // Set or update the storeId field
            };
          }
          return undefined; // If no match is found
        })()
      : undefined;
    if (driverMatchedAccount) {
      setIsAutoDriverEmailSelection(true);
      setSelectedDriverEmail(driverMatchedAccount);
    } else {
      setIsAutoDriverEmailSelection(false);
    }
  }, [driverUserCredsMapping, rtsUserClient]);

  useEffect(() => {
    if (
      fdcDriverAllClientEmails.length > 0 &&
      rtsUserClient &&
      location.storeId
    ) {
      const selectedClientId = rtsUserClient.id;
      // showing only selected client's driver email in dropdown.
      // Filter the emails by the selected client's ID and add storeId to each object
      const updatedEmails = fdcDriverAllClientEmails
        .filter((item) => item.clientId === selectedClientId)
        .map((item) => ({
          ...item,
          storeId: location.storePk, // Set or update the storeId field
        }));
      setFdpDriverClientEmails(updatedEmails);
    }
  }, [fdcDriverAllClientEmails, location]);

  useEffect(() => {
    if (selectedDriverEmail) {
      setDisableSubmitOption(false);
    }
    if (globallySelectedDriverClientEmail && isAutoDriverEmailSelection) {
      setDisableSubmitOption(true);
    }
  }, [selectedDriverEmail, globallySelectedDriverClientEmail]);

  const selectionAsyncSubmitOperation = async () => {
    try {
      const response = await dispatch(
        updateRequestDriverEmail({
          selectedAcc: selectedDriverEmail as FdpDriverClientAccount,
          comment: comment,
        })
      ).unwrap();
      if (response.isSuccess) {
        dispatch(updateFdpDriverSelectedEmailValue(selectedDriverEmail));
        toast.success("RTS submitted successfully!", {
          toastId: "fdpdriverEmailUpdate",
        });
        handleModalClose(true);
        return true;
      }
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpdriverEmailupdate",
        });
        return true;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverEmailUpdateException",
      });
      return false;
    }
    return true;
  };

  const handleClientDeselection = () => {
    if (props.setShowClientSelection) {
      props.setShowClientSelection(true);
    }
    setRtsUserClient(undefined);
    if (globallySelectedDriverClientEmail) {
      selectionAsyncDeleteOperation();
    }
    props.setShowDriverSelection(false);
    setDriverSelectionModalOpen(false);
  };

  const selectionAsyncDeleteOperation = async () => {
    try {
      const response = await dispatch(
        removeFdpDriverAccount(
          globallySelectedDriverClientEmail as FdpDriverClientAccount
        )
      ).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "fdpdriverEmailRemove",
        });
        dispatch(updateFdpDriverSelectedEmailValue(null));
        getfdpDriverEmails();
        return true;
      }
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpdriverEmailRemove",
        });
        return true;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverEmailRemoveException",
      });
      return false;
    }
    return true;
  };

  return (
    <>
      {!isFdpLoading && isFdp && (
        <SelectionModal
          onClose={handleModalClose}
          asyncOperation={selectionAsyncSubmitOperation}
          open={isDriverSelectionModalOpen}
          title={props.newSelection ? "Email ID Selection" : "Edit Email ID"}
          showCancelOption={true}
          disableSubmit={disableSubmitOption}
        >
          <Grid container spacing={1} alignItems="center">
            {rtsUserClient && (
              <>
                <Grid item xs={6} sm={4} md={4} lg={4}>
                  <Typography variant="h6">Selected Client</Typography>
                </Grid>
                <Grid item xs={6} sm={8} md={8} lg={8}>
                  <Chip
                    sx={{ color: "black", fontSize: "14px" }}
                    label={rtsUserClient.clientName}
                    onDelete={handleClientDeselection}
                    deleteIcon={<DeleteIcon />}
                  />
                </Grid>
              </>
            )}
            {globallySelectedDriverClientEmail?.driverEmail && (
              <>
                <Grid item xs={12} sm={3} md={4} lg={4}>
                  <Typography variant="h6">Selected ID</Typography>
                </Grid>
                <Grid item xs={12} sm={9} md={8} lg={8}>
                  <Chip
                    sx={{ color: "black", fontSize: "14px" }}
                    label={
                      fdpDriverClientEmails.find(
                        (x) =>
                          x.driverEmail ===
                          globallySelectedDriverClientEmail.driverEmail
                      )?.driverEmail
                    }
                    onDelete={selectionAsyncDeleteOperation}
                    deleteIcon={<DeleteIcon />}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {isAutoDriverEmailSelection && !globallySelectedDriverClientEmail && (
            <Grid item xs={12} sm={8} md={8} lg={8} mt={1}>
              <TextField
                fullWidth
                id="driverEmailId"
                label="Email ID"
                size="small"
                variant="outlined"
                value={selectedDriverEmail?.driverEmail || ""}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          )}
          {!isAutoDriverEmailSelection && (
            <>
              <Grid item xs={12} sm={8} md={8} lg={8} mt={1}>
                <Typography variant="h6">Select Email ID</Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={8} mt={1}>
                <TextField
                  select
                  fullWidth
                  id="driverId"
                  label="Email ID"
                  size="small"
                  variant="outlined"
                  value={selectedDriverEmail?.driverEmail || ""}
                  onChange={handleChange}
                >
                  {fdpDriverClientEmails.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.driverEmail}
                      disabled={!item.selectable}
                    >
                      {item.driverEmail}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
          {props.routeCheckout && (
            <>
              <Grid
                item
                xs={12}
                sm={8}
                md={8}
                lg={8}
                style={{ marginTop: "10px" }}
              >
                <TextField
                  fullWidth
                  label="Total RTS for the day"
                  value={rtsCount}
                  onChange={handleRtsCountChange}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
                md={8}
                lg={8}
                style={{ marginTop: "10px" }}
              >
                <TextField
                  fullWidth
                  label="Comments"
                  value={comment}
                  onChange={handleCommentChange}
                  inputProps={{
                    maxLength: 45, // Limit the length of the input to 50 characters
                    pattern: ".*", // Pattern to allow alphanumeric characters
                  }}
                ></TextField>
              </Grid>
            </>
          )}
        </SelectionModal>
      )}
    </>
  );
};

export default DriverIDSelection;
